// src/drivers/vis-network-driver.js
//
// Wraps vis-network. Never `import { Network } from 'vis-network'` here, for
// the same reason cytoscape-driver.js never imports cytoscape — the real
// `vis` namespace (whatever exposes `.Network`, whether from a bundler's
// `import * as vis from 'vis-network'` or a CDN `<script>` tag's global
// `vis`) is injected by whoever constructs this driver.
//
// Deliberately built on plain arrays, not `vis.DataSet` — vis-network's own
// `Network` constructor accepts `{nodes: [...], edges: [...]}` as plain
// arrays and wraps them internally, so this driver never needs the separate
// `vis-data` package as a second peer dependency just to hold two arrays.

import { GraphDriver } from '../graph-driver.js';

export class VisNetworkDriver extends GraphDriver {
    /** @param {{Network: Function}} vis_namespace the `vis` module/global itself */
    constructor(vis_namespace) {
        super();

        if (!vis_namespace || typeof vis_namespace.Network !== 'function') {
            throw new Error('VisNetworkDriver needs the real vis-network namespace (with a .Network constructor) — none was given');
        }

        this._vis = vis_namespace;
        this._network = null;
        this._listeners = { click: [], hover: [], select: [], drag: [] };
        // vis-network's own data, kept by this driver rather than read back
        // through `network.body.data` — an internal, version-fragile path —
        // so a click/hover/select/drag payload can carry the caller's own
        // node object, not just the id vis-network itself tracks.
        this._nodes_by_id = new Map();
        this._edges_by_id = new Map();
        this._drag_origin = new Map();
    }

    mount(container, options = {}) {
        this._network = new this._vis.Network(container, { nodes: [], edges: [] }, options.vis_options ?? {});

        this._network.on('click', params => {
            if (params.nodes.length > 0) {
                this._emit_for_ids('click', 'node', params.nodes, this._nodes_by_id);
            } else if (params.edges.length > 0) {
                this._emit_for_ids('click', 'edge', params.edges, this._edges_by_id);
            }
        });

        this._network.on('hoverNode', params => {
            this._emit_for_ids('hover', 'node', [params.node], this._nodes_by_id);
        });

        this._network.on('selectNode', params => {
            this._emit_for_ids('select', 'node', params.nodes, this._nodes_by_id);
        });

        // Same three-phase shape as CytoscapeDriver's grab/drag/free, and
        // for the same reason: 'dragging' has to report a delta since the
        // PREVIOUS tick, not since the drag started, and vis-network's own
        // event only hands back the ids being dragged — the positions have
        // to be read back explicitly to compute that delta.
        this._network.on('dragStart', params => {
            for (const [id, position] of Object.entries(this._network.getPositions(params.nodes))) {
                this._drag_origin.set(id, position);
            }
        });
        this._network.on('dragging', params => {
            const positions = this._network.getPositions(params.nodes);

            for (const id of params.nodes) {
                const now = positions[id];
                const prev = this._drag_origin.get(String(id)) ?? now;
                const delta = { dx: now.x - prev.x, dy: now.y - prev.y };

                this._drag_origin.set(String(id), now);
                this._emit('drag', { type: 'node', id: String(id), data: this._nodes_by_id.get(String(id)) ?? { id }, position: now, delta });
            }
        });
        this._network.on('dragEnd', params => {
            for (const id of params.nodes) {
                this._drag_origin.delete(String(id));
            }
        });
    }

    setData(nodes, edges) {
        this._nodes_by_id = new Map(nodes.map(node => [String(node.id), node]));

        const vis_nodes = nodes.map(node => ({ ...node, label: node.label ?? String(node.id) }));
        const vis_edges = edges.map(edge => {
            const id = `${edge.source}→${edge.target}`;

            this._edges_by_id.set(id, edge);

            return { ...edge, id, from: edge.source, to: edge.target };
        });

        this._network.setData({ nodes: vis_nodes, edges: vis_edges });
    }

    on(event, callback) {
        if (!(event in this._listeners)) {
            throw new Error(`VisNetworkDriver has no "${event}" event — use click, hover, select, or drag`);
        }

        this._listeners[event].push(callback);
    }

    _emit(event, payload) {
        for (const callback of this._listeners[event]) {
            callback(payload);
        }
    }

    _emit_for_ids(event, type, ids, by_id) {
        for (const raw_id of ids) {
            const id = String(raw_id);

            this._emit(event, { type, id, data: by_id.get(id) ?? { id } });
        }
    }

    moveNode(id, dx, dy) {
        let position;

        try {
            position = this._network.getPosition(id);
        } catch {
            return; // unknown id — vis-network throws rather than returning null; silent no-op either way
        }

        // vis-network's own moveNode() takes an ABSOLUTE position, unlike
        // this driver's contract — the read-then-write here is what turns
        // a relative shift into the call vis-network actually wants.
        this._network.moveNode(id, position.x + dx, position.y + dy);
    }

    fit() {
        this._network.fit();
    }

    resize() {
        this._network.redraw();
        this._network.fit();
    }

    destroy() {
        this._network.destroy();
        this._network = null;
    }
}
